/**
 * AIAssistant — botón flotante abajo-derecha que abre un drawer de chat con Gemini.
 * Persistencia de session_id en localStorage por usuario.
 * Detecta la pantalla actual desde window.location.pathname para dar contexto a la IA.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { iaApi } from '../services/api';
import { Button } from './ui/button';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function AIAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('ai_session') || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [lastData, setLastData] = useState(null);
  const [accionesPendientes, setAccionesPendientes] = useState([]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  // Detectar pantalla y context_id desde la URL.
  // contextMap incluye CRM (clientes/contratos/contactos/proyectos/...) + IA (prospeccion) + Visitas y Plan semanal.
  const detectarContexto = () => {
    const p = location.pathname;
    const m = p.match(/^\/(clientes|contratos|proyectos|homologaciones|productos|contactos|tareas|visitas|prospeccion|plan-semanal)\/?([a-f0-9]{24})?/);
    if (!m) return { pantalla: p.replace(/^\//, '') || 'dashboard', context_id: null };
    const rawKind = m[1];
    const kindMap = {
      clientes: 'empresa',
      contratos: 'contrato',
      proyectos: 'proyecto',
      homologaciones: 'homologacion',
      productos: 'producto',
      contactos: 'contacto',
      tareas: 'tarea',
      visitas: 'visita',
      prospeccion: 'prospeccion',
      'plan-semanal': 'plan_semanal',
    };
    return { pantalla: `${kindMap[rawKind] || rawKind}${m[2] ? '/' + m[2] : ''}`, context_id: m[2] || null };
  };

  const enviar = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: txt }]);
    setInput('');
    const { pantalla, context_id } = detectarContexto();
    try {
      const resp = await iaApi.chat({
        session_id: sessionId,
        mensaje: txt,
        pantalla,
        context_id,
      });
      const { session_id, respuesta } = resp;
      if (session_id && !sessionId) {
        setSessionId(session_id);
        localStorage.setItem('ai_session', session_id);
      }
      setMessages((m) => [...m, { role: 'assistant', content: respuesta }]);
      if (resp.datos) setLastData(resp.datos);
      if (resp.acciones_propuestas?.length) {
        setAccionesPendientes(resp.acciones_propuestas);
      } else {
        setAccionesPendientes([]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `⚠ ${e.message}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const nuevaConversacion = () => {
    setSessionId(null);
    localStorage.removeItem('ai_session');
    setMessages([]);
    setAccionesPendientes([]);
    setLastData(null);
  };

  const confirmarAccion = async (accion) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ia/ejecutar-accion-analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          herramienta: accion.herramienta,
          parametros: accion.parametros,
        }),
      });
      setAccionesPendientes([]);
      setMessages((m) => [...m, {
        role: 'assistant',
        content: `✓ Hecho: ${accion.label}`,
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: `⚠ No se pudo ejecutar: ${e.message}`,
        error: true,
      }]);
    }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-xl hover:scale-105 active:scale-95 transition-transform z-40 flex items-center justify-center"
          data-testid="ai-fab"
          title="Asistente IA"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 w-[380px] h-[560px] bg-white rounded-lg shadow-2xl border border-ink-200 flex flex-col z-40"
          data-testid="ai-drawer"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-ink-100 bg-gradient-to-r from-brand-50 to-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-ink-900">Asistente IA</p>
                <p className="text-[10px] text-ink-500">Gemini · Inside CRM</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={nuevaConversacion}
                  className="text-[10px] text-ink-500 hover:text-ink-800 px-2 py-1"
                  data-testid="ai-nueva-conv"
                >
                  nueva
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-ink-400 hover:text-ink-900" data-testid="ai-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.length === 0 && (
              <div className="text-center text-ink-400 mt-8 space-y-3">
                <div className="text-4xl">✨</div>
                <p className="font-medium text-ink-600">¿En qué te ayudo?</p>
                <div className="text-xs space-y-1.5">
                  <p className="bg-ink-50 rounded px-3 py-2 cursor-pointer hover:bg-ink-100" onClick={() => setInput('¿Qué homologaciones caducan en los próximos 90 días?')}>
                    📋 Homologaciones que caducan pronto
                  </p>
                  <p className="bg-ink-50 rounded px-3 py-2 cursor-pointer hover:bg-ink-100" onClick={() => setInput('Dime los proyectos activos por encima de 100.000 €')}>
                    💼 Proyectos grandes en curso
                  </p>
                  <p className="bg-ink-50 rounded px-3 py-2 cursor-pointer hover:bg-ink-100" onClick={() => setInput('¿Quién es mi mejor comercial este mes?')}>
                    📊 Análisis de actividad
                  </p>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                    m.role === 'user'
                      ? 'bg-brand-500 text-white'
                      : m.error
                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                      : 'bg-ink-50 text-ink-900 border border-ink-100'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.content}</p>
                </div>
              </div>
            ))}
            {!sending && accionesPendientes.length > 0 && (
              <div className="space-y-2" data-testid="ai-acciones-propuestas">
                {accionesPendientes.map((accion, i) => (
                  <div
                    key={i}
                    className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-2"
                    data-testid={`ai-accion-${i}`}
                  >
                    <span className="text-xs text-amber-800">{accion.label}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => confirmarAccion(accion)}
                        className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                        data-testid={`ai-accion-confirmar-${i}`}
                      >
                        Confirmar
                      </button>
                      <button
                        onClick={() => setAccionesPendientes([])}
                        className="text-xs px-2 py-1 bg-ink-200 text-ink-600 rounded hover:bg-ink-300"
                        data-testid={`ai-accion-cancelar-${i}`}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-ink-50 px-3 py-2 rounded-lg flex items-center gap-2 text-ink-500">
                  <Loader2 className="w-3 h-3 animate-spin" /> Pensando...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-ink-100">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                placeholder="Escribe una pregunta..."
                className="flex-1 px-3 py-2 rounded-md border border-ink-200 text-sm focus:outline-none focus:border-brand-400"
                disabled={sending}
                data-testid="ai-input"
              />
              <Button onClick={enviar} size="sm" disabled={sending || !input.trim()} data-testid="ai-send">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
